<template>
    <div>
        <h4>Règles AGO</h4>
        <b-card no-body class="shadow-sm mb-3">
            <b-card-body>
                <b-row class="mb-2">

                    <b-col sm="4" class="pr-1">
                        <label class="mt-1 small"> {{ quorumAgoLabel }} (en %)</label>
                        <b-input
                            v-model="form.quorum_ago"
                            size="sm"
                            class="mb-1 text-right"
                            :state="!$v.form.quorum_ago.$invalid"
                            @change="updateLegalExtendedData"
                        />
                    </b-col>
                    <b-col sm="4" class="pr-1 pl-1">
                        <label class="mt-1 small"> {{ majorityAgoLabel }} (en parts)</label>
                        <b-input
                            v-model="form.majority_ago"
                            size="sm"
                            class="mb-1 text-right"
                            :state="!$v.form.majority_ago.$invalid"
                            @change="updateLegalExtendedData"
                        />
                    </b-col>
                </b-row>
                <b-alert
                    variant="success"
                    :show="showSavedAlert"
                    class="mb-0 mt-2"
                >
                    Les modifications ont bien été enregistrées
                </b-alert>
                <b-alert
                    variant="danger"
                    :show="showNotSavedAlert"
                    class="mb-0 mt-2"
                >
                    Une erreur s'est produite, les modifications ne sont pas
                    enregistrées
                </b-alert>
                <b-alert variant="danger" :show="$v.$invalid" class="mb-0 mt-2">
                    Les données ne sont pas valides
                </b-alert>
            </b-card-body>
        </b-card>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { decimal, helpers, integer } from 'vuelidate/lib/validators'
import ToastHandler from '../../../../common/mixins/ToastHandler.vue'

export default {
    name: 'AgoHandler',

    mixins: [ToastHandler],

    data() {
        return {
            form: {
                quorum_ago: null,
                majority_ago: null,
            },
            showSavedAlert: false,
            showNotSavedAlert: false,
            defaultsSynced: false,
        }
    },

    validations: {
        form: {
            quorum_ago: {
                decimal: (value) => !helpers.req(value) || decimal(value),
            },
            majority_ago: {
                integer: (value) => !helpers.req(value) || integer(value),
            },
        },
    },

    async created() {
        this.initForm()
        await this.syncDefaultsOnOpen()
    },

    methods: {
        initForm() {
            this.form.quorum_ago = this.client?.quorum_ago
            this.form.majority_ago = this.client?.majority_ago
        },

        normalizeDecimal(value) {
            if (value === null || value === undefined || value === '') {
                return null
            }

            const normalized = String(value).replace(',', '.')
            const parsed = Number(normalized)
            return Number.isFinite(parsed) ? parsed : null
        },

        normalizeInteger(value) {
            if (value === null || value === undefined || value === '') {
                return null
            }

            const parsed = parseInt(value, 10)
            return Number.isNaN(parsed) ? null : parsed
        },

        async updateLegalExtendedData() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.showSavedAlert = false
                this.showNotSavedAlert = true
                return
            }

            this.showRecordingToast()
            try {
                const payload = {
                    quorum_ago: this.normalizeDecimal(this.form.quorum_ago),
                    majority_ago: this.normalizeInteger(this.form.majority_ago),
                }
                const response = await axios.put(
                    `/legal/clients/${this.client.id}/legal-extended-data`,
                    payload
                )
                this.$store.commit('legalVM/SET_CLIENT', response.data.data)
                this.initForm()
                this.closeToast()
                this.showSavedAlert = true
                this.showNotSavedAlert = false
            } catch (error) {
                this.closeToast()
                this.showSavedAlert = false
                this.showNotSavedAlert = true
                this.handleErrors(error?.response?.status)
            }
        },

        async syncDefaultsOnOpen() {
            if (this.defaultsSynced) {
                return
            }

            const payload = {
                quorum_ago: this.normalizeDecimal(this.form.quorum_ago),
                majority_ago: this.normalizeInteger(this.form.majority_ago),
            }

            try {
                const response = await axios.put(
                    `/legal/clients/${this.client.id}/legal-extended-data`,
                    payload
                )
                this.$store.commit('legalVM/SET_CLIENT', response.data.data)
                this.initForm()
                this.defaultsSynced = true
            } catch (error) {
                // Silent failure on open: keep manual save behavior for user actions.
            }
        },
    },

    computed: {
        ...mapState({
            client: (state) => state.legalVM.client,
        }),

        quorumAgoLabel() {
            return this.client?.legal_extended_data_key_labels?.quorum_ago ?? ''
        },

        majorityAgoLabel() {
            return this.client?.legal_extended_data_key_labels?.majority_ago ?? ''
        },
    },

    watch: {
        client: {
            deep: true,
            handler() {
                this.initForm()
            },
        },
    },
}
</script>

<style scoped></style>
